/**
 * The ch02 feature manifest — pre-season planning and the seasonal buy.
 *
 * Four routes, and the split is reqs/02's own decision chain rather than a menu:
 *
 * | Route | reqs/02 | The question it answers |
 * |---|---|---|
 * | `otb` | §2.1 | What is open to spend, in which period, and against which planned terms |
 * | `clusters` | §2.2 | Which doors behave alike, do the schemes actually discriminate, and what does each cluster over-index on |
 * | `size-curves` | §2.5 | What size mix will actually sell, and how much of the raw history was an artefact of stockouts |
 * | `buy` | §2.6 | What are we ordering, from whom, when — and what bound it |
 *
 * The drill targets follow reqs/07 §3.1's object chain rather than convenience. OTB
 * drills to the buy, because the buy is what consumes it. The cluster screen drills to
 * the size curve, because a curve is estimated per cluster and "which cluster is this
 * curve for" is the first question a merchant asks. The size curve drills to the buy,
 * because reqs/02 §8 requires a purchase-order line traceable to the curve that produced
 * it and a screen that could not walk that link would make the trace a query rather than
 * a click.
 *
 * §2.3 assortment and §2.4 NPI are **deliberately not separate routes yet**. Both are
 * Wave 1 and Wave 3 respectively (12 §4.2), and their planner surface is an editing grid
 * over the option plan with live OTB recalculation — which needs the pinned client bumped
 * to a spec carrying `/api/v1/preseason/assortment/price-edit` before it can be anything
 * other than a mock of an edit. The coupling *preview* is on the OTB screen, where it
 * belongs, because that is the number the edit moves.
 *
 * Every route declares `exports` and `drill`, because the manifest types make both
 * required on a data route — G2 tests *d* and *f* are type errors here rather than census
 * failures later.
 */

import { defineFeature } from '../manifest';
import { PRESEASON_GUIDES } from './guides';

import { buyExport, clusterExport, otbPlanExport, sizeCurveExport } from './exports';

export default defineFeature({
  slug: 'preseason',
  titleKey: 'preseason.title',
  navLabelKey: 'preseason.navLabel',
  icon: 'clipboard-list',  // brand guide p.14
  railGroup: 'future',  // bata-ui#132 — drawn, not offered yet (`disabled` still decides that)
  localeNamespace: 'preseason',
  // Every route's guide and guided tour (ADR 9044) — one entry per route below, or the census fails.
  guides: PRESEASON_GUIDES,
  disabled: true,
  navigation: [
    { labelKey: 'preseason.nav.otb', to: '/preseason/otb', order: 20 },
    { labelKey: 'preseason.nav.clusters', to: '/preseason/clusters', order: 21 },
    { labelKey: 'preseason.nav.sizeCurves', to: '/preseason/size-curves', order: 22 },
    { labelKey: 'preseason.nav.buy', to: '/preseason/buy', order: 23 },
  ],
  routes: [
    {
      path: 'otb',
      titleKey: 'preseason.otb.title',
      urlParams: [],
      component: () => import('./screens/OtbScreen'),
      surfaces: ['table', 'tile'],
      exports: {
        id: 'preseason.otb.grid',
        formats: ['csv', 'xlsx'],
        resolve: otbPlanExport,
      },
      drill: { to: '/preseason/buy', grain: 'week', alsoTo: ['/preseason/clusters'] },
    },
    {
      path: 'clusters',
      titleKey: 'preseason.cluster.title',
      // `?row=` — the scheme version the shape and the doors describe (`./scope.ts`).
      urlParams: ['row'],
      component: () => import('./screens/ClusterScreen'),
      surfaces: ['table', 'chart'],
      tableViewReachable: true,
      exports: {
        id: 'preseason.cluster.grid',
        formats: ['csv', 'xlsx'],
        resolve: clusterExport,
      },
      drill: { to: '/preseason/size-curves', grain: 'cluster' },
    },
    {
      path: 'size-curves',
      titleKey: 'preseason.sizeCurve.title',
      // `?row=` — the curve the chart draws (`./scope.ts`).
      urlParams: ['row'],
      component: () => import('./screens/SizeCurveScreen'),
      surfaces: ['chart', 'tile'],
      tableViewReachable: true,
      exports: {
        id: 'preseason.sizeCurve.chart',
        formats: ['csv', 'xlsx'],
        resolve: sizeCurveExport,
      },
      drill: { to: '/preseason/buy', grain: 'sku' },
    },
    {
      path: 'buy',
      titleKey: 'preseason.buy.title',
      // `?week=` — the receipt week the buy depth is derived for (`./scope.ts`).
      urlParams: ['week'],
      component: () => import('./screens/BuyScreen'),
      surfaces: ['table', 'chart', 'tile'],
      tableViewReachable: true,
      exports: {
        id: 'preseason.buy.grid',
        formats: ['csv', 'xlsx'],
        resolve: buyExport,
      },
      drill: { to: '/preseason/otb', grain: 'article' },
    },
  ],
});
